import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { v4 as generateUploadId } from 'uuid';
import { Datasource, ObjectStorageDatasource } from '../../models/Datasource';
import { VizClient } from '../../api/VizClient';
import DragAndDropInput from './DragAndDropInput';
import { Alert, Progress, Modal } from 'antd';
import { EventEmitter } from '../../utils/EventEmitter';
import { AppNotification } from '../../models/Notification';

type Props = {
  show: boolean;
  onClose: VoidFunction;
  selectedDatasource?: Datasource;
  selectedPath?: string;
  onUploadCompleted?: VoidFunction;
}

export default function UploadModal({ show, onClose, selectedDatasource, selectedPath, onUploadCompleted }: Props) {
  const [percent, setPercent] = useState<number>();
  const [uploadId, setUploadId] = useState<string>();
  const [files, setFiles] = useState<File[]>([]);
  const [folderName, setFolderName] = useState("");

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const fileUploaded = useMemo(() => percent === 100, [percent]);
  // const isRoot = useMemo(() => selectedPath ? selectedPath.split("/").filter(p => p).length === 1 : false, [selectedPath]);

  const isRoot = useMemo(() => {
    // The user is not allowed to upload other dataset folders under folders
    // other then the root.

    // If the datasource has a prefix, then the root folder is at level
    let depthLevel = 1;

    if (selectedDatasource && ((selectedDatasource as ObjectStorageDatasource).prefixPath === null || (selectedDatasource as ObjectStorageDatasource).prefixPath === '/')) {
      // If the datasource doesn't have a prefix, then the root folder is at level 0
      // and the folders at level 1 and beyond are not eligible to host a dataset folder
      depthLevel = 0;
    }

    return selectedPath ? selectedPath.split("/").filter(p => p).length === depthLevel : false
  }, [selectedPath, selectedDatasource]);

  useEffect(() => {
    if (percent === 100) onUploadCompleted?.();
  }, [percent, onUploadCompleted]);

  useEffect(() => {
    EventEmitter.subscribe("JOB_PROGRESS", (newNotification: AppNotification) => {
      if (newNotification.details.messageType === "upload" && newNotification.details.jobId === uploadId) {
        const value = Math.floor(newNotification.details.progress * 100 / newNotification.details.total);
        setPercent(value);
      }
    });

    return () => EventEmitter.unsubscribe("JOB_PROGRESS");
  }, [uploadId]);

  const handleFileUpload = useCallback(() => {
    showLoader();
    const formData = new FormData();
    const newUploadId = generateUploadId();
    setUploadId(newUploadId);
    formData.append('upload_id', newUploadId);
    files.forEach(file => {
      formData.append('files', file, `${isRoot && folderName ? folderName + '/' : ''}${file.name.replace('/', '')}`);
    });

    formData.append('query_details', JSON.stringify({
      host: selectedDatasource!.host,
      accessKey: (selectedDatasource as ObjectStorageDatasource).accessKey,
      secretKey: (selectedDatasource as ObjectStorageDatasource).secretKey,
      bucket: (selectedDatasource as ObjectStorageDatasource).bucket,
      port: selectedDatasource!.port,
      secure: (selectedDatasource as ObjectStorageDatasource).secure,
      prefixPath: selectedPath || (selectedDatasource as ObjectStorageDatasource).prefixPath || "",
      regionName: (selectedDatasource as ObjectStorageDatasource).regionName,
    }));

    VizClient.uploadFile(formData)
      .then(
        () => hideLoader(),
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('file-upload-error', message, `Upload Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [selectedDatasource, files, hideLoader, showLoader, showToast, selectedPath, folderName, isRoot]);

  const progressFormat = useCallback(() => percent + '%', [percent]);

  const handleClose = useCallback(() => {
    if (fileUploaded) {
      setPercent(undefined);
      setUploadId(undefined);
      setFiles([]);
    }
    onClose();
  }, [onClose, fileUploaded]);

  const handleFolderNameChange = useCallback((evt: any) => setFolderName(evt.target.value), []);

  return (
    <Modal
      open={show}
      onClose={handleClose}
      onCancel={handleClose}
      width={600}
      title="Upload files"
      footer={
        <div className="d-flex justify-content-between">
          <Button onClick={handleClose} size="sm" variant="light" className="border">Cancel</Button>
          {percent === undefined && (
            <Button disabled={files.length === 0 || (isRoot && !folderName)} onClick={handleFileUpload} size="sm" variant="success">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Upload files</span>
            </Button>
          )}
        </div>
      }
    >
      {percent === undefined
        ? (
          <>
            {isRoot && (
              <Form.Group controlId="folderName" className="mb-3">
                <Form.Label>Folder Name</Form.Label>
                <Form.Control required size="sm" placeholder="Choose folder name..." value={folderName} onChange={handleFolderNameChange} />
                <Form.Control.Feedback type="invalid">Folder Name is required.</Form.Control.Feedback>
                <Form.Text>Files cannot be uploaded in the root folder. A folder with the specified name will be created instead.</Form.Text>
              </Form.Group>
            )}
            <Form.Group controlId="file" className="mb-3">
              <DragAndDropInput
                onChange={setFiles}
                onFolderChange={setFolderName}
                title="Drop files or folders here"
                subtitle={
                  <>
                    If a folder is dropped/selected, its content will be uploaded under that location.
                  </>
                }
              />
            </Form.Group>
          </>
        )
        : (
          <div className="my-4">
            <Progress status={fileUploaded ? "success" : "normal"} format={progressFormat} percent={percent} />
            {fileUploaded && (
              <>
                <Alert
                  className="mt-3"
                  type="success"
                  message={
                    <div className="success-message">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#52c41a" className="w-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      <h6 className="text-center mt-2" style={{ color: "#52c41a" }}>File{files.length > 0 && 's'} successfully uploaded!</h6>
                    </div>
                  }
                />
              </>
            )}
          </div>
        )}
    </Modal>
  )
}